<template>
  <div
    id="flashcard-view-container-v2"
    ref="flashcard-view-container"
    class="flashcard-view-container"
  >
    <breadcrumbs
      :breadcrumbs="breadcrumbs"
      version="v2"
    ></breadcrumbs>
    <template
      v-if="answered"
    >
      <answered-flashcard
        :answer-ids="answerIds"
        :category="currentFlashcardCategory"
        :correct="correct"
        :flashcard="currentFlashcard"
        :raw-answer-data="rawAnswerData"
      ></answered-flashcard>
      <answered-footer
        :show-flashcard-statuses="isScenario"
        @tag="tagFlashcard"
      ></answered-footer>
    </template>

    <template
      v-else
    >
      <unanswered-header
        :category="currentFlashcardCategory"
        :flashcard="currentFlashcard"
      ></unanswered-header>
      <unanswered-flashcard
        :answer-ids="answerIds"
        :category="currentFlashcardCategory"
        :flashcard="currentFlashcard"
        :raw-answer-data="rawAnswerData"
        :sharing-enabled="sharingEnabled"
        :terminology="isTerminology"
        @set-answer-ids="setAnswerIds"
        @set-raw-answer-data="setRawAnswerData"
        @try-answer-flashcard="tryAnswerFlashcard"
      ></unanswered-flashcard>
      <unanswered-footer
        :can-submit-answer="canSubmitAnswer"
        :category="currentFlashcardCategory"
        :flashcard="currentFlashcard"
        :disable-next="!canSubmitAnswer"
        :disable-previous="disablePrevious"
        :show-next-override="shouldShowNext"
        @answer="answerFlashcard"
        @next-flashcard="handleNextFlashcard"
        @previous-flashcard="handlePreviousFlashcard"
      ></unanswered-footer>
    </template>
  </div>
</template>

<script>

import AnsweredFlashcard from '@/components/flashcards-v2/AnsweredFlashcard';
import AnsweredFooter from '@components/flashcards-v2/AnsweredFooter';
import Breadcrumbs from '@components/singletons/breadcrumbs';
import UnansweredFlashcard from '@components/flashcards-v2/UnansweredFlashcard';
import UnansweredFooter from '@components/flashcards-v2/UnansweredFooter';
import UnansweredHeader from '@/components/flashcards-v2/UnansweredHeader.vue';
import CategoriesMixin from '@/mixins/categories';
import ElapsedTimeMixin from '@/mixins/elapsedTime';
import FlashcardViewMixin from '@/mixins/flashcardViews';
import GlobalMixin from '@/mixins/global';
import RateLimitMixin from '@/mixins/rate-limit';
import QuizMixin from '@/mixins/quizzes';
import QuestionsUIV2Mixin from '@/mixins/questions-ui-v2';

export default {
  components: { AnsweredFlashcard, AnsweredFooter, Breadcrumbs, UnansweredFlashcard, UnansweredFooter, UnansweredHeader },
  mixins: [
    CategoriesMixin,
    ElapsedTimeMixin,
    FlashcardViewMixin,
    GlobalMixin,
    RateLimitMixin,
    QuizMixin,
    QuestionsUIV2Mixin,
  ],
  computed: {
    breadcrumbs() {
      if (!this.currentFlashcardCategory) return [];
      let categories = this.currentFlashcardCategory.path.split('.').map(id => this.$store.getters['categories/categories'][id]);
      if (this.isScenario) {
        return categories.map(c => {
          return { name: c.name, route: { name: 'scenarios#category', params: { categoryTypeId: c.categoryTypeId, categoryId: c.id }, query: this.currentQuery } };
        });
      }
      let crumbs = categories.map(c => {
        return { name: c.name, route: {name: `${this.routePrefix}#index`, params: { categoryId: c.id}, query: this.currentQuery}};
      });
      return crumbs;
    },
    canSubmitAnswer() {
      return this.hasBeenAnswered;
    },
    disablePrevious() {
      const isFirst = this.currentFlashcard?.id === this.firstUnansweredFlashcardId;
      return isFirst ? false : !this.canSubmitAnswer;
    },
    shouldShowNext() {
      if (!this.currentFlashcard?.id) {
        return false;
      }
      return this.currentFlashcard?.id !== this.firstUnansweredFlashcardId;
    },
    currentFlashcardCategory() {
      if (this.currentFlashcard.id) {
        let categoryId = this.$route.params.categoryId;
        return this.$store.getters['categories/categories'][categoryId];
      }
    },
    reviewAfterFinish() {
      return this.currentFlashcardCategory.reviewAfterFinish;
    },
    dashboard() {
      const lastDashboardIds = this.$store.getters['dashboards/lastVisitedId'];
      if (Array.isArray(lastDashboardIds) && lastDashboardIds.length > 0) {
        return this.$store.getters['dashboards/get'][lastDashboardIds[0]];
      }
      return this.$store.getters['dashboards/menuDefaultDashboard'];
    },
    nextFlashcardRoute() {
      if (this.nextFlashcard) {
        const suffix = this.questionUiV2Enabled ? '-v2' : '';
        return {
          name: `${this.routePrefix}#flashcard${suffix}`,
          params: { categoryId: this.$route.params.categoryId, flashcardId: this.nextFlashcard },
          query: this.currentQuery,
        };
      } else {
        return {
          name: `${this.routePrefix}#results`,
          params: { categoryId: this.$route.params.categoryId },
          query: this.currentQuery,
        };
      }
    },
    previousFlashcardRoute() {
      if (this.previousFlashcard) {
        const suffix = this.questionUiV2Enabled ? '-v2' : '';
        return {
          name: `${this.routePrefix}#flashcard${suffix}`,
          params: { categoryId: this.$route.params.categoryId, flashcardId: this.previousFlashcard },
          query: this.currentQuery,
        };
      } else {
        return {
          name: `${this.routePrefix}#index`,
          params: { categoryId: this.$route.params.categoryId },
          query: this.currentQuery,
        };
      }
    },
    sharingEnabled() {
      const enabled = !!(this.$store.getters['productVars/get']['flashcardShareMessage']);
      return enabled && !this.onTour;
    },
    shouldShowRateLimitModal() {
      if (!this.flashcardIsRateLimited(this.currentFlashcard, this.currentFlashcardCategory)) {
        return false;
      }

      if (!this.creditLimitReached) {
        return false;
      }

      return true;
    },
    skipRationale() {
      // don't show rationales for standard quizzes and custom quizzes with reviewAfterFinish
      if (this.currentResource.destination) {
        return (this.currentResource.destination == 'custom_quiz' && this.currentFlashcardCategory.reviewAfterFinish) || this.currentResource.destination == 'quiz_categories';
      };
      // Assume that rationales should be skipped if no destination is present (in the case of deeplinked quizzes)
      return true;
    },
    isScenario() {
      return this.$route.name && this.$route.name.includes('scenario');
    },
  },
  watch: {
    '$route.params.flashcardId': function(id) {
      if(!this.$store.getters['flashcards/flashcards'][id]) {
        let params = { ids: [id], rateLimited: this.rateLimitingEnabled };
        this.$store.dispatch('flashcards/fetchFlashcards', params);
      }
    },
    'currentFlashcard.id': {
      immediate: true,
      handler: function(id) {
        this.resetFlashcardFromState(id);
      },
    },
  },
  created() {
    let stateParams = ['flashcards', 'stats'];
    this.$store.dispatch('flashcardStates/fetchStates', stateParams);
    let params = {};

    params.categoryId = this.$route.params.categoryId;

    if (this.$route.params.flashcardId) {
      params.flashcardId = this.$route.params.flashcardId;
    }

    params.rateLimited = this.rateLimitingEnabled;

    this.sessionPromise = this.$store.dispatch('flashcards/createUserQuizFlashcardSession', params);
  },
  mounted() {
    this.sessionPromise.then(flashcard => {
      if(flashcard) {
        this.reorderSessionForScenarioBox();
        const categoryId = this.$route.params.categoryId;
        const suffix = this.questionUiV2Enabled ? '-v2' : '';
        const targetFlashcardId = (this.isScenario && this.firstUnansweredFlashcardId) || flashcard.id;

        this.$router.replace({
          name: `${this.routePrefix}#flashcard${suffix}`,
          params: { categoryId, flashcardId: targetFlashcardId },
          query: this.currentQuery,
        });
        this.$nextTick().then(() => {
          if (this.shouldShowRateLimitModal) {
            this.$modal.show('rate-limit-reached', {
              category: this.currentFlashcardCategory,
              flashcard: this.currentFlashcard,
            });
          }
        });
      } else {
        const categoryId = this.$route.params.categoryId;
        const category = this.$store.getters['categories/categories'][categoryId];
        const flashcardStateList = this.$store.getters['flashcardStates/flashcardStateList']
          .filter(fs => fs.categoryId == categoryId);
        const flashcardCount = category?.counts?.total ?? 0;
        const quizCompleted = flashcardCount > 0 && flashcardStateList.length >= flashcardCount;

        if (category && quizCompleted) {
          this.completeQuiz(categoryId, flashcardCount)
            .then(() => this.$router.go(-1));
        }
      }
    }).catch(e => {
      this.$router.go(-1);
      Promise.reject(e);
    });
  },
  methods: {
    reorderSessionForScenarioBox() {
      if (!this.isScenario) return;
      const categoryId = parseInt(this.$route.params.categoryId);
      const grouping = this.$store.getters['groupings/list'].find(g => {
        return g?.groupingType === 'Scenario Box'
          && g?.productAssociations.find(pa => pa.categoryId === categoryId);
      });
      if (!grouping) return;
      const flashcardOrder = grouping.flashcardAssociations.map(aa => aa.assetBId);
      const session = [...this.$store.getters['flashcards/currentSession']];
      session.sort((a, b) => {
        const indexA = flashcardOrder.indexOf(a);
        const indexB = flashcardOrder.indexOf(b);
        return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
      });
      this.$store.commit('flashcards/setFlashcardSession', session);
    },
    categoryCount(categoryId) {
      return this.$store.getters['categories/categories'][categoryId].counts.total;
    },
    tryAnswerFlashcard() {
      if (this.canSubmitAnswer) {
        this.answerFlashcard();
      }
    },
    answerFlashcard() {
      // Load next flashcard
      if (!this.$store.getters['flashcards/flashcards'][this.nextFlashcard] && this.nextFlashcard) {
        let params = { ids: this.nextFlashcardGroup, rateLimited: this.rateLimitingEnabled };
        this.$store.dispatch('flashcards/fetchFlashcards', params);
      }

      // Evaluate the answer
      const correct = this.evaluateAnswer(this.currentFlashcard, this.answerIds, this.rawAnswerData);
      this.correct = correct;
      this.$analytics.events.answeredFlashcard(this.$store, this.currentFlashcardCategory, this.currentFlashcard, this.answerIds, this.rawAnswerData, correct, this.isQotD, this.dashboard?.name);

      this.createCategoryState();

      let params = {
        timeInSeconds: this.elapsedTimeInSeconds(), // from mixin
        rateLimited: this.rateLimitingEnabled,
      };

      this.createFlashcardState(params);
      if (this.reviewAfterFinish) {
        this.decrementRateLimitCredits(this.currentFlashcard, this.currentFlashcardCategory);
      }

      // If not showing rationale, just go to next question
      if (this.skipRationale) {
        this.goToNextFlashcard();
      } else {
        this.answered = true;
      }
    },
    createCategoryState() {
      const params = {
        number: this.quizFlashcardStates(this.$store.getters['categories/categories'][this.$route.params.categoryId]).length + 1,
        categoryStatusId: this.nextFlashcard ? this.inProgressStatus.id : this.completedStatus.id,
        categoryId: this.$route.params.categoryId,
        mobileCreatedAt: Date.now(),
        rateLimited: this.rateLimitingEnabled,
      };

      this.$store.dispatch('categoryStates/sendCategoryState', params);
    },
    handleNextFlashcard() {
      if (this.answerChanged && this.canSubmitAnswer) {
        this.answerFlashcard();
      }
      this.goToNextFlashcard();
    },
    handlePreviousFlashcard() {
      if (this.answerChanged && this.canSubmitAnswer) {
        this.answerFlashcard();
      }
      this.goToPreviousFlashcard();
    },
  },
};
</script>

<style lang='scss' scoped>
  #flashcard-view-container-v2 {
    @include flashcard-view-container-v2;
  }
</style>
